import { useState, useEffect } from 'react';
import { useAuth, api } from '@/lib/api';

const roleConfig = {
  '管理员': { color: 'bg-blue-50 text-blue-700 border border-blue-100', icon: 'ri-shield-user-fill', desc: '可管理家庭所有设置、成员及规则', bg: 'bg-blue-500' },
  '记录员': { color: 'bg-emerald-50 text-emerald-700 border border-emerald-100', icon: 'ri-edit-box-fill', desc: '可记录日常奖惩操作，无法修改规则', bg: 'bg-emerald-500' }
};

// Avatar color by relation
const relationAvatarConfig: Record<string, { bg: string; icon: string }> = {
  '父亲': { bg: 'bg-blue-500', icon: 'ri-user-fill' },
  '母亲': { bg: 'bg-pink-400', icon: 'ri-user-fill' },
  '爷爷': { bg: 'bg-amber-500', icon: 'ri-user-fill' },
  '奶奶': { bg: 'bg-orange-400', icon: 'ri-user-fill' },
  '外公': { bg: 'bg-teal-500', icon: 'ri-user-fill' },
  '外婆': { bg: 'bg-rose-400', icon: 'ri-user-fill' },
  '其他长辈': { bg: 'bg-violet-400', icon: 'ri-user-fill' }
};

const identityOptions = ['父亲', '母亲', '爷爷', '奶奶', '外公', '外婆', '其他长辈'];

// Normalize role: 1=管理员, 2=记录员
function normalizeRole(role: number): '管理员' | '记录员' {
  if (role === 1) return '管理员';
  return '记录员';
}

// Convert role name to number
function roleToNumber(role: string): number {
  return role === '管理员' ? 1 : 2;
}

interface EditAdult {
  id: string;
  name: string;
  relation: string;
  role: string;
}

export default function AdultsTab() {
  const { currentFamily, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editAdult, setEditAdult] = useState<EditAdult | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [newAdult, setNewAdult] = useState({ name: '', phone: '', relation: '父亲', role: '记录员' });

  const canManage = currentFamily?.my_role === 1;

  // 加载家庭成员列表
  useEffect(() => {
    const fetchMembers = async () => {
      if (!currentFamily?.id) return;
      try {
        setLoading(true);
        const res = await api.family.getFamilyMembers(currentFamily.id);
        if (res.code === 0) {
          setMembers(res.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch family members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [currentFamily?.id]);

  // 获取已被占用的身份列表
  const usedIdentities = members.map(m => m.identity).filter(Boolean);
  const isIdentityUsed = (identity: string) => usedIdentities.includes(identity);

  const handleEdit = (member: any) => {
    const name = member.display_name || member.user_nickname || '';
    const relation = member.identity || '其他长辈';
    const role = normalizeRole(member.role);
    setEditAdult({ id: member.id, name, relation, role });
  };

  const handleDelete = (memberId: string) => {
    setShowDeleteConfirm(memberId);
  };

  const confirmDelete = async () => {
    if (!currentFamily?.id || !showDeleteConfirm) return;
    setSubmitting(true);
    try {
      const res = await api.family.deleteFamilyMember(currentFamily.id, showDeleteConfirm);
      if (res.code === 0) {
        setMembers(prev => prev.filter(m => m.id !== showDeleteConfirm));
        setShowDeleteConfirm(null);
      }
    } catch (error) {
      console.error('Failed to delete member:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitInvite = async () => {
    if (!currentFamily?.id || !newAdult.phone) return;
    setSubmitting(true);
    try {
      const res = await api.family.addFamilyMember(currentFamily.id, {
        phone: newAdult.phone,
        role: roleToNumber(newAdult.role),
        identity: newAdult.relation,
        display_name: newAdult.name || undefined
      });
      if (res.code === 0) {
        setMembers(prev => [...prev, res.data]);
        setShowAddModal(false);
        setNewAdult({ name: '', phone: '', relation: '父亲', role: '记录员' });
      }
    } catch (error) {
      console.error('Failed to invite member:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!currentFamily?.id || !editAdult) return;
    setSubmitting(true);
    try {
      const res = await api.family.updateFamilyMember(currentFamily.id, editAdult.id, {
        role: roleToNumber(editAdult.role),
        identity: editAdult.relation,
        display_name: editAdult.name || undefined
      });
      if (res.code === 0) {
        setMembers(prev => prev.map(m => m.id === editAdult.id ? res.data : m));
        setEditAdult(null);
      }
    } catch (error) {
      console.error('Failed to update member:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-blue-500 animate-spin mb-2"></i>
          <p className="text-gray-400 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-bold text-blue-900 text-sm flex items-center gap-1.5">
          <i className="ri-parent-fill text-blue-500"></i>
          家长成员 ({members.length})
        </span>
        <button
          onClick={() => {
            if (!canManage) {
              alert('记录员无权邀请成员，仅管理员可操作');
              return;
            }
            setShowAddModal(true);
          }}
          className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${
            canManage
              ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:opacity-90 cursor-pointer'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <i className="ri-add-line"></i>邀请成员
        </button>
      </div>

      {/* Role Description */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-500">
              <i className="ri-shield-user-fill text-white text-sm"></i>
            </div>
            <span className="font-bold text-blue-700 text-sm">管理员</span>
          </div>
          <p className="text-xs text-blue-600">可管理家庭设置、成员、奖惩规则、积分商城等所有功能</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-500">
              <i className="ri-edit-box-fill text-white text-sm"></i>
            </div>
            <span className="font-bold text-emerald-700 text-sm">记录员</span>
          </div>
          <p className="text-xs text-emerald-700">可进行日常奖惩记录和积分兑换操作，无法修改规则设置</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {members.map(member => {
          const normalizedRole = normalizeRole(member.role);
          const cfg = roleConfig[normalizedRole];
          const relation = member.identity || '其他长辈';
          const avatarCfg = relationAvatarConfig[relation] || { bg: 'bg-blue-400', icon: 'ri-user-fill' };
          const name = member.display_name || member.user_nickname || member.user_phone || '用户';
          const isSelf = user && member.user_id === user.id;
          return (
            <div key={member.id} className="bg-white rounded-2xl p-4 border border-blue-50">
              <div className="flex items-start gap-3 mb-4">
                {/* Color block avatar with relation text */}
                <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${avatarCfg.bg}`}>
                  <i className={`${avatarCfg.icon} text-white text-base`}></i>
                  <span className="text-white text-[9px] font-bold mt-0.5 leading-none">{relation}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-bold text-blue-900 text-sm">{name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5 ${cfg.color}`}>
                      <i className={`${cfg.icon} text-xs`}></i>
                      {normalizedRole}
                    </span>
                    {isSelf && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">
                        我
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs">{relation} · {member.user_phone || ''}</p>
                </div>
              </div>

              <div className={`rounded-xl p-2.5 mb-3 ${normalizedRole === '管理员' ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                <p className="text-xs text-gray-600">{cfg.desc}</p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <i className="ri-time-fill text-gray-300 text-xs"></i>
                  <span className="text-xs text-gray-400">加入时间：{new Date(member.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => handleEdit(member)}
                    className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-200 text-gray-500 text-xs rounded-lg hover:border-blue-400 hover:text-blue-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line text-xs"></i>编辑
                  </button>
                  {!isSelf && (
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="w-7 h-7 flex items-center justify-center border border-red-100 text-red-400 text-xs rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-6-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {members.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-user-star-line text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-400 text-sm">暂无家长成员</p>
        </div>
      )}

      {/* Edit Modal */}
      {editAdult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditAdult(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[400px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-edit-line text-blue-500"></i>编辑成员信息
              </h3>
              <button onClick={() => setEditAdult(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">成员姓名</label>
                <input
                  value={editAdult.name}
                  onChange={e => setEditAdult({ ...editAdult, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">身份关系</label>
                <div className="grid grid-cols-3 gap-2">
                  {identityOptions.map(rel => (
                    <button
                      key={rel}
                      onClick={() => setEditAdult({ ...editAdult, relation: rel })}
                      className={`py-2 rounded-xl text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                        editAdult.relation === rel ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500'
                      }`}
                    >
                      {rel}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">账号权限</label>
                <div className="flex gap-2">
                  {(['管理员', '记录员'] as const).map(r => (
                    <button
                      key={r}
                      onClick={() => setEditAdult({ ...editAdult, role: r })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                        editAdult.role === r
                          ? r === '管理员' ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-emerald-500 text-emerald-600 bg-emerald-50'
                          : 'border-gray-200 text-gray-500'
                      }`}
                    >
                      <i className={`${roleConfig[r].icon} text-sm`}></i>{r}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <i className="ri-information-fill text-blue-400"></i>
                  {editAdult.role === '管理员' ? '可管理家庭所有设置、成员及规则' : '可记录奖惩操作，无法修改规则'}
                </p>
              </div>
              <button
                onClick={handleSubmitEdit}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? '保存中...' : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[420px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-user-add-fill text-blue-500"></i>邀请家庭成员
              </h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">成员姓名</label>
                <input value={newAdult.name} onChange={e => setNewAdult({ ...newAdult, name: e.target.value })} placeholder="请输入姓名" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">手机号码</label>
                <input type="tel" value={newAdult.phone} onChange={e => setNewAdult({ ...newAdult, phone: e.target.value })} placeholder="请输入手机号" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">身份关系</label>
                <div className="grid grid-cols-3 gap-2">
                  {identityOptions.map(r => {
                    const isUsed = isIdentityUsed(r);
                    return (
                      <button
                        key={r}
                        onClick={() => !isUsed && setNewAdult({ ...newAdult, relation: r })}
                        disabled={isUsed}
                        className={`py-2 rounded-xl text-xs font-medium border-2 transition-all whitespace-nowrap ${
                          isUsed
                            ? 'border-gray-100 text-gray-300 bg-gray-50 cursor-not-allowed'
                            : newAdult.relation === r
                              ? 'border-blue-500 text-blue-600 bg-blue-50 cursor-pointer'
                              : 'border-gray-200 text-gray-500 cursor-pointer hover:border-gray-300'
                        }`}
                      >
                        {r}
                        {isUsed && <span className="text-[9px] block text-gray-300">已占用</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">* 直系亲属身份每个家庭只能有一位</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">账号权限</label>
                <div className="flex gap-2">
                  {(['管理员', '记录员'] as const).map(r => (
                    <button key={r} onClick={() => setNewAdult({ ...newAdult, role: r })} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${newAdult.role === r ? r === '管理员' ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-emerald-500 text-emerald-600 bg-emerald-50' : 'border-gray-200 text-gray-500'}`}>
                      <i className={`${roleConfig[r].icon} text-sm`}></i>{r}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={handleSubmitInvite} disabled={submitting || !newAdult.phone} className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? '发送中...' : '发送邀请'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[360px]" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-6-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="font-bold text-blue-900 mb-2">确认删除</h3>
              <p className="text-gray-500 text-sm">确定要删除该家庭成员吗？此操作不可恢复。</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                disabled={submitting}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
